<template>
  <div class="container-fluid">
    <PageHeader
      :page="page"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />

    <FilterManager
      ref="filterManagerRef"
      v-model="filterModel"
      class="filter-manager_extended"
      search-container-class="search-btn"
      :filters="filtersConfig"
      :open-advanced="showAdvanced"
      @search="onSearchHandler($event, 1)"
      @academicYear="onAcademicYearChangeHandler"
      @center="onCenterChangeHandler"
      @municipality="onMunicipalityChangeHandler"
    />

    <SourceLegend />

    <CustomSeparator class="my-4" text="Resultado de Evaluaciones" />

    <template v-if="results.length">
      <ResultRow
        v-for="(result, index) in results"
        :key="`row-${index}`"
        class="mb-3"
        :value="result"
        @refresh-search="refreshSearchHandler"
      />

      <template v-if="showPagination">
        <Pagination
          :data="pagination"
          show-text
          @pagination-go-page="goToPageHandler"
        />
      </template>
    </template>

    <template v-else>
      <CustomResultNotFound />
    </template>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import Pagination from 'pagination';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import ResultRow from './components/ResultRow.vue';
import SourceLegend from './components/SourceLegend.vue';

import alertMixin from '@/mixin/alert.mixin';
import storeMixin from '@/mixin/storeGenerator';
import {
  getInitFilters as _getInitFilters,
  getCenters as _getCenters,
  getMunicipalities as _getMunicipalities,
  getSchools as _getSchools,
  getHistory as _getHistory,
} from '@/modules/student-evaluations-discussion/api.js';
import {
  sources as evaluationSources,
  evaluationStatusString,
  evaluationStatusCodes,
} from '@/utils/constants/evaluation-discussion/evaluationStatusCodes';

import alerts from '@/utils/constants/evaluation-discussion/alerts';

const DEFAULT_PAGE_SIZE = 20;
const initialFilterModel = () => ({
  academicYear: null,
  source: null,
  status: null,
  center: null,
  municipality: null,
  school: null,
  evaluationType: null,
  disciplineType: null,
  alert: null,
  studentSie: null,
});

export default {
  name: 'MainEvaluationDiscussHistory',
  components: {
    PageHeader,
    FilterManager,
    Pagination,
    CustomSeparator,
    CustomResultNotFound,
    ResultRow,
    SourceLegend,
  },
  mixins: [storeMixin, alertMixin],
  data: () => ({
    page: {
      icon: 'fas fa-user-edit',
      title: 'Discusión de Evaluaciones y Recomendaciones',
      routes: [
        'Inicio',
        'Estudiante',
        'Discusión de Evaluaciones y Recomendaciones',
      ],
    },
    filterModel: { ...initialFilterModel() },
    filterOptions: {
      academicYears: [],
      sources: [],
      statuses: [],
      centers: [],
      municipalities: [],
      schools: [],
      evaluationTypes: [],
      disciplineTypes: [],
      alerts: [],
    },
    pagination: {
      currentPage: 1,
      totalItemCount: 0,
      pageCount: 1,
      count: 0,
    },
    pageSize: DEFAULT_PAGE_SIZE,
    results: [],
    academicYearValidation: '',
    showAdvanced: false,
  }),
  computed: {
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          value: this.filterModel.academicYear,
          options: this.filterOptions.academicYears,
          placeholder: 'Año Académico',
          fieldName: 'description',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          validations: this.academicYearValidation,
        },
        {
          name: 'source',
          component: 'CustomDropDown',
          value: this.filterModel.source,
          options: this.filterOptions.sources,
          placeholder: 'Tipo de Bandeja',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          value: this.filterModel.status,
          options: this.statusesOptions,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
        },
        {
          name: 'studentSie',
          component: 'CustomInput',
          icon: 'fa fa-search color-orange',
          leftIcon: true,
          placeholder: 'Escriba Número de SIE',
          class: 'col-12 col-md-3',
        },
        {
          name: 'center',
          component: 'CustomDropDown',
          label: 'Centro:',
          value: this.filterModel.center,
          options: this.filterOptions.centers,
          placeholder: 'Seleccionar',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          isAdvanced: true,
          disabled: this.filterOptions.centers.length == 1,
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          label: 'Municipio:',
          value: this.filterModel.municipality,
          options: this.filterOptions.municipalities,
          placeholder: 'Seleccionar',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          isAdvanced: true,
        },
        {
          name: 'school',
          component: 'CustomDropDown',
          label: 'Escuela:',
          value: this.filterModel.school,
          options: this.filterOptions.schools,
          placeholder: 'Seleccionar',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          isAdvanced: true,
        },
        {
          name: 'evaluationType',
          component: 'CustomDropDown',
          label: 'Tipo de evaluación:',
          value: this.filterModel.evaluationType,
          options: this.filterOptions.evaluationTypes,
          placeholder: 'Seleccionar',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          isAdvanced: true,
        },
        {
          name: 'disciplineType',
          component: 'CustomDropDown',
          label: 'Disciplina:',
          value: this.filterModel.disciplineType,
          options: this.filterOptions.disciplineTypes,
          placeholder: 'Seleccionar',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          isAdvanced: true,
        },
        {
          name: 'alert',
          component: 'CustomDropDown',
          label: 'Alertas:',
          value: this.filterModel.alert,
          options: this.filterOptions.alerts,
          placeholder: 'Seleccionar',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          isAdvanced: true,
        },
      ];
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pageSize;
    },
    statusesOptions() {
      if (this.filterModel.source?.id === evaluationSources.discussionPending) {
        return this.filterOptions.statuses.filter(
          (x) => x.id === evaluationStatusCodes.pending
        );
      }

      return this.filterOptions.statuses;
    },
  },

  watch: {
    'filterModel.academicYear'(val) {
      if (val) {
        this.academicYearValidation = '';
      }
    },
    'filterModel.studentSie'(val) {
      if (val) {
        this.academicYearValidation = '';
      }
    },
    'filterModel.source'(val) {
      if (!val) {
        this.filterModel.status = null;
      } else {
        if (val.id === evaluationSources.discussionPending) {
          this.selectPendingStatus();
        } else {
          this.selectPendingStatus(false);
        }
      }
    },
  },

  methods: {
    async getInitFilters() {
      const { data } = await _getInitFilters();

      this.filterOptions = {
        ...this.filterOptions,
        ...data,
      };

      if (data.centers && data.centers.find((x) => x.selected)) {
        this.filterModel.center = data.centers.find((x) => x.selected);
      }

      if (data.municipalities && data.municipalities.find((x) => x.selected)) {
        this.filterModel.municipality = data.municipalities.find(
          (x) => x.selected
        );
      }

      if (data.schools && data.schools.find((x) => x.selected)) {
        this.filterModel.school = data.schools.find((x) => x.selected);
      }
    },

    async onAcademicYearChangeHandler(value) {
      this.filterOptions = {
        ...this.filterOptions,
        centers: [],
        municipalities: [],
        schools: [],
      };
      this.filterModel = {
        ...this.filterModel,
        center: null,
        municipality: null,
        school: null,
      };

      if (!value?.id) return;

      this.filterOptions.centers = (await _getCenters(value.id)).data;
      this.filterOptions.municipalities = (
        await _getMunicipalities(value.id)
      ).data;
      this.filterOptions.schools = (await _getSchools(value.id)).data;

      if (this.filterOptions.centers.length == 1) {
        this.filterModel.center = this.filterOptions.centers[0];
      }

      if (this.filterOptions.municipalities.length == 1) {
        this.filterModel.municipality = this.filterOptions.municipalities[0];
      }

      if (this.filterOptions.schools.length == 1) {
        this.filterModel.school = this.filterOptions.schools[0];
      }
    },

    async onCenterChangeHandler(value) {
      const academicYearId = this.filterModel.academicYear?.id;

      this.filterOptions = {
        ...this.filterOptions,
        municipalities: [],
        schools: [],
      };

      this.filterModel = {
        ...this.filterModel,
        municipality: null,
        school: null,
      };

      if (!academicYearId) return;

      this.filterOptions.municipalities = (
        await _getMunicipalities(academicYearId, value?.id)
      ).data;
      this.filterOptions.schools = (
        await _getSchools(academicYearId, value?.id)
      ).data;

      if (this.filterOptions.municipalities.length == 1) {
        this.filterModel.municipality = this.filterOptions.municipalities[0];
      }

      if (this.filterOptions.schools.length == 1) {
        this.filterModel.school = this.filterOptions.schools[0];
      }
    },

    async onMunicipalityChangeHandler(value) {
      const academicYearId = this.filterModel.academicYear?.id;
      const centerId = this.filterModel.center?.id;

      this.filterOptions = {
        ...this.filterOptions,
        schools: [],
      };

      this.filterModel = {
        ...this.filterModel,
        school: null,
      };

      if (!academicYearId) return;

      this.filterOptions.schools = (
        await _getSchools(academicYearId, centerId ?? null, value?.id)
      ).data;

      if (this.filterOptions.schools.length == 1) {
        this.filterModel.school = this.filterOptions.schools[0];
      }
    },

    async onSearchHandler(filterModel, pageIndex = 1) {
      this.academicYearValidation = '';

      const isValid = await this.$refs.filterManagerRef.$refs.formValidator.validate();
      if (!isValid) {
        this.ShowToast('Alerta', 'Información Incompleta.', 'error');
        return;
      }

      let searchParams = {
        academicYear: filterModel.academicYear?.id,
        studentSie: filterModel.studentSie,
        source: filterModel.source?.id,
        status: filterModel.status?.id,
        evaluationType: filterModel.evaluationType?.id,
        disciplineType: filterModel.disciplineType?.id,
        alert: filterModel.alert?.id,
        center: filterModel.center?.id,
        municipality: filterModel.municipality?.id,
        school: filterModel.school?.id,
      };

      if (filterModel.alert != null) {
        var alert = alerts.find((x) => x.id == filterModel.alert.id);

        searchParams = {
          timeToExpirationStart: alert.timeToExpirationStart,
          timeToExpirationEnd: alert.timeToExpirationEnd,
          ...searchParams,
        };
      }

      if (filterModel.alert != null) {
        var alert = alerts.find((x) => x.id == filterModel.alert.id);

        searchParams = {
          timeToExpirationStart: alert.timeToExpirationStart,
          timeToExpirationEnd: alert.timeToExpirationEnd,
          ...searchParams,
        };
      }

      const { data: res } = await _getHistory(
        searchParams,
        pageIndex,
        this.pageSize
      );

      this.results = [...res.data];

      this.pagination = {
        totalItemCount: res.totalItemCount,
        pageCount: res.pageCount,
        currentPage: res.currentPage,
        count: res.count,
      };
    },

    async goToPageHandler(page) {
      await this.onSearchHandler(this.filterModel, page);
    },
    async refreshSearchHandler() {
      await this.onSearchHandler(this.filterModel, this.pagination.currentPage);
    },

    selectPendingStatus(selecting = true) {
      const status = this.filterModel.status;
      this.filterModel.status = selecting
        ? {
            name: evaluationStatusString[evaluationStatusCodes.pending],
            sortOrder: 1,
            id: evaluationStatusCodes.pending,
            selected: true,
            disabled: true,
            isDirty: false,
            keyId: '00000000-0000-0000-0000-000000000000',
          }
        : {
            name: status?.name ?? '',
            sortOrder: 1,
            id: status?.id ?? '',
            selected: true,
            disabled: false,
            isDirty: false,
            keyId: '00000000-0000-0000-0000-000000000000',
          };
    },
  },

  async mounted() {
    await this.getInitFilters();

    this.showAdvanced = this.filtersConfig.some(
      (filter) => filter.value != null && filter.isAdvanced
    );
  },

  destroyed() {
    this.persistData();
  },

  beforeRouteEnter(_, from, next) {
    const persistRoutes = [
      'MainEvaluationDiscussHistory',
      'MainEvaluationDiscuss',
    ];

    next((vm) => {
      if (persistRoutes.some((x) => x == from.name)) {
        vm.recoveryData();
        vm.onSearchHandler(vm.filterModel, vm.pagination.currentPage);
      } else {
        vm.shouldRecovery = false;
        vm.showAdvanced = false;
        vm.filterModel = { ...initialFilterModel() };
      }
    });
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .orange-icon {
    padding-right: 0 !important;
    font-size: 18pt !important;
    margin-left: 5px !important;
  }

  .page-header__extended {
    &--left-side {
      .tag-badge {
        margin-bottom: 1rem;
      }
      .controls-wrap {
        display: none;
      }
    }
  }
  .filter-manager_extended {
    border-radius: 0.25rem;
    @media only screen and (min-width: 750px) {
      .search-btn {
        &.col-lg-3 {
          flex: 0 0 15%;
          max-width: 15%;
          padding-left: 0;
        }
      }
    }
  }
}
</style>
